// management-list.tsx — Surface 2 of the Drip Campaigns redesign: the campaigns
// management LIST. Ported faithfully from the design handoff's
// `prototype/src/drip-management.jsx` (header + Audiences/Templates/Campaigns
// tabs + filter pills + responsive card grid; single-message cards stay clean,
// multi-step cards grow the Layers row + step-dot glyph + next-follow-up note +
// stops-on-reply/recurring indicator + footer stats).
//
// Unlike the prototype (which reads `window.DripData` mock), this is wired to
// the REAL data layer:
//   • drips        — `dripsSelectByQuery(DBDripReducer, …)`, fetched via DBDripThunks.find
//   • step stats   — each drip is run as a sequence of REAL campaigns tagged
//                    `dripId`; we fetch those (`DBCampaignThunks.find({ dripId:{$in} })`)
//                    and aggregate `sentCount` per drip. There is NO campaign-level
//                    "replied" field (replies live in per-message ack/logs, which a
//                    list view deliberately does not load), so Replied renders "—".
//   • audience     — board name + the SAME contact-count selector the campaign
//                    audience list uses (`getTaskSumByBoardIdRecord`).
//
// Rendered inside the `.campaigns-rd` wrapper supplied by the route shell, so
// all design tokens + utility classes (.btn/.pill/.num/…) are in scope. The
// prototype's inline-style approach is kept for fidelity.

import React, { useEffect, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { namespace } from '@common/config/constants';
import { Layers, Reply, Clock, Search, Plus, Megaphone, ArrowUp, ArrowDown, LayoutGrid, List, Bookmark } from 'lucide-react';
import { useIsMobile } from '@common/hooks/use-is-mobile';
import { effectiveCampaignView } from './effective-campaign-view';
import {
  sortCampaignCards,
  coerceCampaignSort,
  collectAudienceOptions,
  cardMatchesAudience,
  coerceAudienceId,
  ALL_AUDIENCES_ID,
  campaignSortLabel,
  DEFAULT_CAMPAIGN_SORT,
  type CampaignSortId,
} from './campaign-list-sort';
import {
  coerceCampaignView,
  campaignViewLabel,
  DEFAULT_CAMPAIGN_VIEW,
  type CampaignViewMode,
} from './campaign-list-view';
import { UserMessage } from '@bt-common/schemas/bt/user-message';
import { CampaignMessageBubble, templateToPreviewMessage, isEmptyPreview } from './campaign-message-bubble';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Virtuoso } from 'react-virtuoso';

import type { Drip, DripStatus, DripStep, DripStepGap } from '@bt-common/schemas/bt/drip';
import type { Template } from '@bt-common/schemas/bt/campaign';
import type { Campaign } from '@common/models/campaign-model';
import {
  DBDripThunks,
  dripsSelectByQuery,
  dripsSelectOneObjectByQuery,
  DBCampaignThunks,
  campaignsSelectByQuery,
  campaignsSelectOneObjectByQuery,
  DBBoardsThunks,
  DBTasksThunks,
} from '@common-reducers/DBServiceThunks';
import {
  getTaskSumByBoardIdRecord,
  getBoardNameByBoardIdRecord,
} from '@common-reducers/CampaignSelectors';
import { resolveCampaignCardStatus } from '@common/services/campaign-status';
import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import { chunkIds } from '@common-reducers/in-query';
import {
  StatusLabel,
  AssetBadge,
  AudAvatar,
  ListSkeleton,
  type AssetKind,
  type CampaignStatus as CardStatus,
} from '@extension/context/content/components/campaigns-redesign/primitives';
import { TemplateList } from '@extension/context/content/components/campaigns-redesign/template-list';
import { FirstRunEmptyState } from '@extension/context/content/components/campaigns-redesign/empty-state';
import { AudienceList } from '@extension/context/content/components/campaigns-redesign/audience-list';
import { ActionsMenu } from './action-menu';
import { SaveAsDripTemplateModal } from './save-as-drip-template-modal';
import { deriveAudienceLabel, toAudienceIds } from './audience-selection';
import { useDripActions } from './use-drip-actions';
import { useBulkDripActions } from './use-bulk-drip-actions';
import { deriveDripStatus } from './drip-status';
import { deriveDripDisplayStatus } from './drip-display-status';
import { nextUpcomingDripSendAt, resolveListDate, type ListDateKind } from './campaign-schedule';
import { formatStartedRaw } from './campaign-list-date';
import { ROW_COLUMNS, ROW_GRID, alignFor } from './campaign-list-columns';
import { useCampaignsRedesignNav } from './nav-context';
import { CampaignContentEditModal } from './campaign-content-edit-modal';
import { MessageConfirmationDialog } from '@extension/context/content/components/add-scheduled-message-button/message-confirmation-dialog';
import { flowPath } from '@common/utils/clear-flow';
import { GuideVideoButton } from '@/common/components/guide-video/guide-video-button';

// Human-readable drip gap. The drip step gap reuses the campaign
// RecurrenceUnit enum (minute…year), which is wider than the primitive
// `gapText`'s DelayUnit — so format locally to cover month/year too.
function dripGapText(gap?: DripStepGap): string {
  if (!gap) return '';
  const n = gap.value;
  return `${n} ${gap.unit}${n === 1 ? '' : 's'}`;
}

// `$in` fetches go through the shared `chunkIds` (≤20 ids per batch, backend qs arrayLimit — #803).

// ─── Sent count (per message) ──────────────────────────────────────────────
// A campaign doc carries NO `sentCount`, so the old `campaign.sentCount ?? 0`
// always rendered "0". The list also deliberately doesn't load per-message logs,
// so we can't read the delivered/read receipt split here — but we DON'T need to
// for a headline "Sent" count: once a (step-)campaign reaches a terminal
// `complete-*` state it dispatched to ALL its recipients, and each campaign
// froze that audience size as `contactCount`. So "messages sent" for one message
// = dispatched ? contactCount : 0. A series applies the SAME rule PER message and
// sums — steps still sending (running/pending) contribute 0 until they complete,
// mirroring the detail's per-step "reached WhatsApp". (A step's own `contactCount`
// already accounts for stop-on-reply drop-off, so summing is exact, not an
// over-count.)
// The terminal "sent" states — MUST match campaignCardStatus's completed set
// (note `complete-send`, no trailing "t", is the most common persisted value).
function campaignIsDispatched(status?: string): boolean {
  return status === 'complete-sent' || status === 'complete-delivered'
    || status === 'complete-send' || status === 'completed';
}
// Recipients a dispatched (step-)campaign sent to: its frozen `contactCount`, or
// — for older campaigns that never recorded one — the live audience-board count
// (`fallbackAudience`, the SAME value the Contacts stat falls back to). 0 until
// the campaign reaches a terminal `complete-*` state.
function sentForCampaign(c: Pick<Campaign, 'campaignStatus' | 'contactCount'>, fallbackAudience = 0): number {
  if (!campaignIsDispatched(c.campaignStatus ?? undefined)) return 0;
  const cc = c.contactCount ?? 0;
  return cc > 0 ? cc : fallbackAudience;
}

// ─── Card view-model ─────────────────────────────────────────────────────
// The prototype card consumed a flat mock shape; we map each `Drip` (+ its
// step-campaigns + board) into the same shape so the markup ports verbatim.
interface CardModel {
  id: string;
  /** `drip` = multi-step sequence (drips collection); `campaign` = an
   *  existing standalone campaign (no dripId). Drives the open() routing. */
  kind: 'drip' | 'campaign';
  name: string;
  audienceName: string;
  audienceCount: number | undefined;
  audienceColor: string;
  /** FE#339 — the board ids this card targets (drips can target several, #161;
   *  empty = no audience). Drives the dedicated audience/board filter dropdown,
   *  which is distinct from the free-text search. */
  boardIds: string[];
  steps: number;
  currentStep: number;
  // A drip uses DripStatus; a standalone campaign is normalised (via
  // getCampaignRealStatus) into the same lifecycle vocabulary the
  // StatusLabel map understands (pending/running/paused/…).
  status: DripStatus | CardStatus;
  /** The status the badge SHOWS — a future-dated, not-yet-started drip reads
   *  'scheduled' ("Scheduled") instead of the logical 'active' ("Running · 0 sent").
   *  Display-only: `status` stays the logical value that StepGlyph + the lifecycle
   *  filter read, so filtering/glyphs are unchanged. */
  displayStatus: DripStatus | CardStatus | 'scheduled';
  stopOnReply: boolean;
  recurring: boolean;
  sent: number;
  /** No campaign-level reply count exists without loading per-message acks. */
  replied: number | undefined;
  /** Configured gap before the next follow-up (not a live countdown — the
   *  doc has no prior-step completion time). Empty when none/last step. */
  nextStepGap: string;
  badges: AssetKind[];
  /** The message this campaign sends (first step for a drip), previewed on the
   *  card via the shared WhatsApp PreviewMessageBubble. */
  previewMessage: Partial<UserMessage>;
  /** Whether the source doc's template is loaded yet. False → the card shows a
   *  preview skeleton (the template can load separately from the list row). */
  hasTemplate: boolean;
  /** Lower-cased haystack for the list search: the campaign name plus EVERY
   *  message it sends (all drip steps, not just the previewed first one). */
  searchText: string;
  /** The formatted date string shown in the DATE column (empty ⇒ render "—"). */
  startedAt: string;
  /** What that date MEANS for this row (FE#340): a completed row's date is when it
   *  SENT, a pending row's is when it is SCHEDULED, etc. Drives the per-row label so
   *  the column is no longer a semantics-mixing generic "Date". */
  dateKind: ListDateKind;
  /** Sort key — newest first across the merged drips + campaigns set. */
  createdAtMs: number;
  /** Sort key for the user-facing date sort. Derived from the SAME resolved date the
   *  `startedAt` label shows (via resolveListDate), so ordering matches the date the
   *  row actually displays — sorting by createdAt while showing the send date would
   *  look broken for a campaign scheduled far ahead. */
  scheduledAtMs: number;
}

const AUD_COLORS = ['blue', 'violet', 'green', 'amber', 'cyan', 'rose'] as const;

// ─── Empty-draft hygiene ──────────────────────────────────────────────────
// `/campaigns/new` persists a `status:'draft'` shell immediately and the wizard
// autosaves into it, so every visit (or abandoned wizard) can leave a content-
// less draft behind. An "empty draft" is one with NO audience AND NO real step
// content — pure clutter we hide from the list (FIX 1) and delete on wizard exit
// (FIX 2, see wizard.tsx). A draft with an audience OR any step carrying a real
// message / asset / poll is a "real draft": kept, shown, resumable.
//
// Exported so the wizard's exit-deletion reuses the SAME predicate (DRY) — the
// list and the deleter must agree exactly on what "empty" means.
function stepHasContent(step: DripStep): boolean {
  const tpl = step?.template as Template | undefined;
  return !!(tpl?.message?.trim() || tpl?.asset?.url || tpl?.isPoll || tpl?.poll);
}

export function isEmptyDraft(drip: Pick<Drip, 'status' | 'boardIDArray' | 'steps'>): boolean {
  if (drip.status !== 'draft') return false;
  if (drip.boardIDArray?.length) return false;
  return !(drip.steps ?? []).some(stepHasContent);
}

// Distinct asset kinds across all steps of a drip, derived from each step's
// template (media → image/video, poll, buttons).
function badgesForSteps(steps: Drip['steps']): AssetKind[] {
  const kinds = new Set<AssetKind>();
  for (const step of steps ?? []) {
    const tpl = step.template as Template | undefined;
    const assetType = tpl?.asset?.type ?? '';
    if (tpl?.asset?.url) {
      kinds.add(/video/i.test(assetType) ? 'video' : 'image');
    }
    if (tpl?.isPoll || tpl?.poll) kinds.add('poll');
    if (tpl?.buttons && tpl.buttons.length > 0) kinds.add('buttons');
  }
  return Array.from(kinds);
}

function toMs(raw?: string | Date): number {
  if (!raw) return 0;
  const t = new Date(raw).getTime();
  return Number.isNaN(t) ? 0 : t;
}

// FE#340 — human label for each date semantic. The list column used to be a
// generic "Date"; now every cell names what its date IS (Sent / Scheduled / …).
//
// This was a module-level map of English, so a Hebrew reader got a correctly
// translated status chip on the card and a raw "SCHEDULED" in its footer, one
// above the other. Resolved per render instead, like every other label here.
// (`textTransform: uppercase` in `DateCell` is a display choice, not part of the
// string — Hebrew has no case, so it is a no-op there.)
// Written as four inline t() calls with literal keys rather than a key table, so
// every key stays statically readable by `translation-keys-exist` — indexing a
// table would be an unresolvable call site and add to that guard's debt.
const dateKindLabel = (
  t: TFunction,
  kind: Exclude<ListDateKind, 'none'>,
): string => {
  switch (kind) {
    case 'sent': return t('campaignsRedesign.dateKind.sent', 'Sent');
    case 'scheduled': return t('campaignsRedesign.dateKind.scheduled', 'Scheduled');
    case 'started': return t('campaignsRedesign.dateKind.started', 'Started');
    default: return t('campaignsRedesign.dateKind.created', 'Created');
  }
};

/** The DATE column cell: a small semantic label + the resolved date, shared by the
 *  compact row and the card footer so both read identically. An empty/`none` date
 *  renders the em-dash placeholder. */
const DateCell: React.FC<{ kind: ListDateKind; text: string; style?: React.CSSProperties }> = ({ kind, text, style }) => {
  const { t } = useTranslation(namespace);
  if (!text || kind === 'none') return <span style={style}>—</span>;
  return (
    <span style={{ ...style, display: 'inline-flex', alignItems: 'baseline', gap: 5, minWidth: 0 }}>
      <span
        style={{
          fontSize: 9.5, fontWeight: 600, letterSpacing: '.03em', textTransform: 'uppercase',
          color: 'var(--muted-2)', flexShrink: 0,
        }}
      >
        {dateKindLabel(t, kind)}
      </span>
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{text}</span>
    </span>
  );
};

// Asset badges for a single (standalone-campaign) template — mirrors
// badgesForSteps but for one message. Accepts a loose template shape so it works
// across both the bt-common and campaign-model Template variants.
function badgesForTemplate(tpl?: {
  asset?: { url?: string | null; type?: string | null } | null;
  isPoll?: boolean;
  poll?: unknown;
  buttons?: unknown[];
} | null): AssetKind[] {
  const kinds = new Set<AssetKind>();
  const assetType = tpl?.asset?.type ?? '';
  if (tpl?.asset?.url) kinds.add(/video/i.test(assetType) ? 'video' : 'image');
  if (tpl?.isPoll || tpl?.poll) kinds.add('poll');
  if (tpl?.buttons && tpl.buttons.length > 0) kinds.add('buttons');
  return Array.from(kinds);
}

// `templateToPreviewMessage` / `isEmptyPreview` / `PreviewableTemplate` now live
// in `./campaign-message-bubble` (shared with the Templates list, wizard and
// sequence detail) so every campaign surface previews a template identically.

// Normalise a campaign's raw `campaignStatus` into the lifecycle vocabulary the
// StatusLabel map understands. `getCampaignRealStatus` collapses the various
// complete-* states into Running/Completed; map its PascalCase result back to
// the lowercase keys used by STATUS_MAP. A campaign with no status is a draft.
function campaignCardStatus(campaign: Campaign): CardStatus {
  // Full resolution (incl. the FE#256 `expired` promotion) lives in the pure, unit-tested
  // resolveCampaignCardStatus. Here we only adapt a Campaign onto its input shape: recurrence lives
  // on the schedule (`schedule.isRecurring`), and last activity is `updatedAt` (falling back to the
  // schedule due date) — a frozen value far in the past is the tell that a `running` campaign ended.
  return resolveCampaignCardStatus(
    {
      campaignStatus: campaign.campaignStatus,
      contactCount: campaign.contactCount,
      isRecurring: campaign.schedule?.isRecurring,
      lastActivityAt: campaign.updatedAt ?? campaign.schedule?.dueDate,
    },
    new Date(),
  );
}

// ─── StepGlyph ───────────────────────────────────────────────────────────
// Mini step-progress glyph: a dot per step, filled = done, ringed = current.
export interface StepGlyphProps {
  steps: number;
  currentStep: number;
  // Only renders for multi-step drips, but the card status vocabulary is wider
  // (a standalone campaign may be pending/running) — accept the union.
  status: DripStatus | CardStatus;
}
export const StepGlyph: React.FC<StepGlyphProps> = ({ steps, currentStep, status }) => {
  if (steps <= 1) return null;
  return (
    <span
      style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}
    >
      {Array.from({ length: steps }).map((_, i) => {
        // A completed drip has sent EVERY step, so every dot is green — the
        // `currentStep` cursor can lag at the last-but-one index, which would
        // otherwise leave the final (already-sent) step showing grey.
        const done = status === 'completed' || i < currentStep;
        const cur = i === currentStep && (status === 'active' || status === 'paused');
        return (
          <span
            key={i}
            style={{
              width: cur ? 8 : 6,
              height: cur ? 8 : 6,
              borderRadius: '50%',
              background: done ? 'var(--success)' : cur ? 'var(--bt-blue)' : 'var(--line-strong)',
              boxShadow: cur ? '0 0 0 3px var(--bt-blue-soft)' : 'none',
              transition: 'all .2s',
            }}
          />
        );
      })}
    </span>
  );
};

// ─── Stat (footer metric) ──────────────────────────────────────────────────
export interface StatProps {
  label: string;
  value: string;
  sub?: string | null;
  accent?: string | null;
}
export const Stat: React.FC<StatProps> = ({ label, value, sub, accent }) => (
  <div style={{ display: 'flex', flexDirection: 'column' }}>
    <span style={{ fontSize: 9.5, color: 'var(--muted-2)', textTransform: 'uppercase', letterSpacing: '.04em', fontWeight: 600 }}>
      {label}
    </span>
    <span style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
      <span className="num" style={{ fontSize: 14, fontWeight: 600, color: accent || 'var(--ink)' }}>{value}</span>
      {sub && <span className="num" style={{ fontSize: 10.5, color: 'var(--muted)' }}>{sub}</span>}
    </span>
  </div>
);

// ─── CampaignCard ───────────────────────────────────────────────────────────
export interface CampaignCardProps {
  c: CardModel;
  onOpen: (card: CardModel) => void;
  /** #103: open the scoped in-flight message editor for a launched campaign.
   *  Owned by the LIST, not the card: the cards are virtualized, so a modal
   *  rendered by a card would be unmounted the moment its row scrolled out of
   *  the viewport — and this modal's unmount is a real abort request. */
  onEditContent?: (campaignId: string) => void;
  /** #103: open the scheduled-campaign editor (message + audience + send time)
   *  for a standalone campaign that has not started sending. Owned by the LIST
   *  for the same reason as `onEditContent` — the cards are virtualized. */
  onEditSchedule?: (campaignId: string) => void;
  /** #590: show a selection checkbox (bulk pause/stop). Only drip cards are
   *  selectable — the bulk mutations take a drip id. */
  selectable?: boolean;
  selected?: boolean;
  onToggleSelect?: (id: string) => void;
}


// Shimmer block primitive (styled by `.skel` in campaigns-redesign.css).
const Skel: React.FC<{ w: number | string; h: number; style?: React.CSSProperties }> = ({ w, h, style }) => (
  <div className="skel" style={{ width: w, height: h, ...style }} />
);

// Placeholder for the message preview, shown inside a card while that card's
// template is still loading — same footprint as the real WhatsApp bubble so the
// swap to loaded content doesn't shift the layout.
const PreviewSkeleton: React.FC = () => {
  const { t } = useTranslation(namespace);
  return (
    <div
      className="skel"
      style={{ width: '70%', maxWidth: 460, minWidth: 200, height: 58, borderRadius: '4px 12px 12px 12px' }}
      aria-label={t('campaignsRedesign.loadingMessagePreview', 'Loading message preview')}
    />
  );
};

// Cap the message-preview column so a tall promo (image + long caption) can't
// balloon a single row far past its neighbours — the list should read as an
// even stack of comparable cards.
const PREVIEW_MAX_H = 200;

export const CampaignCard: React.FC<CampaignCardProps> = ({ c, onOpen, onEditContent, onEditSchedule, selectable, selected, onToggleSelect }) => {
  // Mobile: stack the identity + message-preview columns (the fixed 340px LEFT
  // beside the preview overflows a 390px screen).
  const isMobile = useIsMobile();
  const multi = c.steps > 1;
  // Whether the preview will likely exceed PREVIEW_MAX_H and get clipped — media
  // (a fixed ~200px box) or a poll always do; plain text only when it's long.
  // Drives the bottom fade so short bubbles aren't needlessly dimmed.
  const previewMayOverflow =
    c.hasTemplate && !isEmptyPreview(c.previewMessage) &&
    (!!c.previewMessage.asset || !!c.previewMessage.poll || (c.previewMessage.message?.length ?? 0) > 180);

  // Overflow actions — driven by the SAME hook the detail header uses. Resolve
  // the source docs the way the list/detail do: a drip by its own id, a
  // standalone campaign by its own id. `isStandalone` splits the two, and the
  // redesign nav's `editCampaign` opens the wizard (Edit + Duplicated copy).
  const { t } = useTranslation(namespace);
  const nav = useCampaignsRedesignNav();
  const isStandalone = c.kind === 'campaign';
  const realDrip = useAppSelector((s) =>
    c.kind === 'drip' ? dripsSelectOneObjectByQuery(s.DBDripReducer, { _id: c.id }) : undefined,
  ) as Drip | undefined;
  const standaloneCampaign = useAppSelector((s) =>
    isStandalone ? campaignsSelectOneObjectByQuery(s.DBCampaignReducer, { _id: c.id }) : undefined,
  ) as Campaign | undefined;
  // The effective drip fed to the hook: the real doc for a drip; a minimal
  // name-bearing bridge for a standalone (the standalone branch only reads
  // `.name`, for the rename seed — all its mutations go through the campaign).
  // For a drip, feed the hook the card's ALREADY-derived status (via the shared
  // `deriveDripStatus`) instead of the raw stored one — so a fully-sent drip's
  // card menu drops Pause/Resume too, matching the detail header.
  const effectiveDrip: Drip | undefined = isStandalone
    ? (standaloneCampaign ? { _id: standaloneCampaign._id, name: standaloneCampaign.name } : undefined)
    : (realDrip ? { ...realDrip, status: c.status as DripStatus } : undefined);

  const { items, renaming, nameInput, setNameInput, commitRename, cancelRename, savingTemplate, stopSaveTemplate, confirmingDelete, confirmDelete, cancelDelete } = useDripActions({
    drip: effectiveDrip,
    realDrip,
    standaloneCampaign,
    dripId: c.id,
    isStandalone,
    onEdit: (id) => nav?.editCampaign(id),
    // #103: "Edit message" on a launched campaign — standalone campaigns are
    // the majority path and have no pause/resume control anywhere else in the
    // product, so this menu item is their only route back from a paused state.
    onEditContent,
    // #103: and "Edit campaign" for a standalone that has not started sending —
    // the affordance the customer report was about.
    onEditSchedule,
  });

  return (
    <div style={{ position: 'relative' }}>
    <SaveAsDripTemplateModal open={savingTemplate} drip={realDrip} onClose={stopSaveTemplate} />
    {confirmingDelete && (
      <MessageConfirmationDialog
        title={t('campaignsRedesign.deleteDialog.title', 'Delete this campaign and stop sending?')}
        description={t('campaignsRedesign.deleteDialog.body', 'Anyone who has not received it yet never will. Messages already sent cannot be recalled.')}
        confirmLabel={t('campaignsRedesign.deleteDialog.confirm', 'Stop and delete')}
        destructive
        onClose={(value?: string) => { if (value !== undefined) confirmDelete(); else cancelDelete(); }}
      />
    )}
    <button
      onClick={() => onOpen(c)}
      style={{
        appearance: 'none', textAlign: 'start', cursor: 'pointer', width: '100%',
        border: '1px solid var(--line)', borderRadius: 12, background: 'var(--surface)',
        padding: 0, overflow: 'hidden', transition: 'border-color .12s, box-shadow .12s',
        display: 'flex', flexDirection: 'column',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = 'var(--line-strong)';
        e.currentTarget.style.boxShadow = '0 4px 14px rgba(11,18,32,.06)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = 'var(--line)';
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      <div style={{ padding: '13px 15px', flex: 1, width: '100%', boxSizing: 'border-box', display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 12 : 16, alignItems: 'flex-start' }}>
        {/* LEADING: identity + step affordances. The inline-end padding on mobile
            clears the absolutely-positioned "…" menu (top:10, insetInlineEnd:12)
            so the status pill in the top row doesn't slide under it. It must be
            LOGICAL: the menu mirrors in RTL, so a physical paddingRight would
            leave the gap on the wrong side and the menu would land on the pill. */}
        <div style={{ flex: isMobile ? '1 1 auto' : '0 0 340px', width: isMobile ? '100%' : undefined, minWidth: 0, paddingInlineEnd: isMobile ? 34 : undefined }}>
        {/* top row */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          {/* #590: bulk-selection checkbox. Nested inside the card button the
              same way the inline rename input is (stopPropagation so the click
              toggles selection without opening the card). Only drip cards get it. */}
          {selectable && (
            <input
              type="checkbox"
              checked={!!selected}
              onClick={(e) => e.stopPropagation()}
              onChange={() => onToggleSelect?.(c.id)}
              aria-label={t('campaignsRedesign.bulk.selectRow', 'Select campaign')}
              style={{ marginTop: 4, cursor: 'pointer', flexShrink: 0 }}
            />
          )}
          {/* Uniform campaign glyph: every card shows the same green campaign
              (megaphone) icon (the multi-step distinction still reads from the
              "N messages" Layers row + step glyph below), so the list isn't a
              rainbow of per-audience tints. */}
          <AudAvatar color="green" icon={Megaphone} size={36} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {renaming ? (
                // Inline rename — reuse the detail's pattern: onBlur commits,
                // Enter commits, Escape cancels. stopPropagation on click/key so
                // typing (and the click that focused it) never opens the card.
                <input
                  autoFocus
                  value={nameInput}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => setNameInput(e.target.value)}
                  onBlur={commitRename}
                  onKeyDown={(e) => {
                    e.stopPropagation();
                    if (e.key === 'Enter') commitRename();
                    else if (e.key === 'Escape') cancelRename();
                  }}
                  placeholder={t('campaignsRedesign.campaignNamePlaceholder', 'Campaign name')}
                  style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--ink)', border: '1px solid var(--bt-blue-line)', borderRadius: 6, padding: '1px 6px', background: 'var(--surface)', outline: 'none', minWidth: 0, flex: 1 }}
                />
              ) : (
                <span style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {c.name}
                </span>
              )}
            </div>
          </div>
          <StatusLabel status={c.displayStatus ?? c.status} />
        </div>

        {/* multi-step affordance row */}
        {multi ? (
          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginTop: 12, padding: '7px 10px', background: 'var(--surface-2)', border: '1px solid var(--line)', borderRadius: 8 }}>
            <Layers size={13} stroke="var(--bt-blue-deep)" />
            <span style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--ink-2)' }}>{t('campaignsRedesign.counts.messages', { count: c.steps })}</span>
            <StepGlyph steps={c.steps} currentStep={c.currentStep} status={c.status} />
            {(c.status === 'active' || c.status === 'paused') && (
              <span style={{ fontSize: 11, color: 'var(--muted)' }}>
                {c.status === 'paused'
                  ? t('campaignsRedesign.card.pausedOnStep', 'paused on step {{current}} of {{total}}', { current: c.currentStep + 1, total: c.steps })
                  : t('campaignsRedesign.card.onStep', 'on step {{current}} of {{total}}', { current: c.currentStep + 1, total: c.steps })}
              </span>
            )}
            <div style={{ flex: 1 }} />
            {c.stopOnReply && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 10.5, color: 'var(--wa-deep)' }}>
                <Reply size={11} /> {t('campaignsRedesign.card.stopsOnReply', 'stops on reply')}
              </span>
            )}
          </div>
        ) : (
          <div style={{ height: 12 }} />
        )}

        {/* next follow-up (configured gap before the upcoming step) */}
        {multi && c.nextStepGap && c.status === 'active' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8, fontSize: 11.5, color: 'var(--bt-blue-deep)' }}>
            <Clock size={12} />{' '}
            <Trans
              t={t}
              i18nKey="campaignsRedesign.card.nextFollowUp"
              defaults="Next follow-up <num>{{gap}}</num> after the previous message"
              values={{ gap: c.nextStepGap }}
              components={{ num: <span className="num" style={{ fontWeight: 600 }} /> }}
            />
          </div>
        )}
        </div>

        {/* RIGHT: message preview — the shared CampaignMessageBubble, fed the
            campaign's template directly so media / poll / links render exactly as
            in the composer, right-aligned like the Templates list.

            Capped to PREVIEW_MAX_H and clipped so a big image + long caption
            (e.g. a promo card) can't balloon one row to 3× its neighbours — the
            list should read as an even stack. A soft bottom fade signals the
            clip, rendered only when the content actually overflows the cap
            (media, a poll, or a long message) so short bubbles aren't dimmed. */}
        {/* The inline-end padding reserves room for the absolutely-positioned "…"
            menu (top:10, insetInlineEnd:12) so the end-aligned bubble shifts away
            and the menu sits OUTSIDE it rather than overlapping its corner.
            LOGICAL, not paddingRight: the menu already mirrors via insetInlineEnd,
            so a physical padding put the reserved gap on the opposite side in RTL
            and the menu covered the bubble it was supposed to clear. */}
        <div style={{ flex: isMobile ? '1 1 auto' : '1 1 0', width: isMobile ? '100%' : undefined, minWidth: 0, position: 'relative', maxHeight: PREVIEW_MAX_H, overflow: 'hidden', paddingInlineEnd: 36 }}>
          {!c.hasTemplate
            ? <PreviewSkeleton />
            : isEmptyPreview(c.previewMessage)
              ? <span style={{ fontSize: 12.5, color: 'var(--muted)', fontStyle: 'italic' }}>{t('campaignsRedesign.wizard.noMessageSet', 'Nothing written yet')}</span>
              : <CampaignMessageBubble message={c.previewMessage} align="end" />}
          {previewMayOverflow && (
            <div
              aria-hidden
              style={{
                position: 'absolute', left: 0, right: 0, bottom: 0, height: 48,
                background: 'linear-gradient(to bottom, transparent, var(--surface))',
                pointerEvents: 'none',
              }}
            />
          )}
        </div>
      </div>

      {/* footer stats */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '10px 15px', background: 'var(--surface-2)' }}>
        {/* Was two bare stats — "CONTACTS 200" and "SENT 43" — leaving the
            reader to do the division that is the actual question ("how far
            along is this?"). And the em-dash under CONTACTS did not mean zero:
            it meant the audience board had not resolved, which is a different
            fact and looked like one. One stat with a denominator answers the
            question directly, and falls back to the bare count only when the
            denominator genuinely is not known. */}
        <Stat
          label={t('campaignsRedesign.card.sentLabel', 'Sent')}
          value={c.sent.toLocaleString()}
          sub={c.audienceCount === undefined
            ? undefined
            : t('campaignsRedesign.card.outOfContacts', 'of {{total}}', { total: c.audienceCount.toLocaleString() })}
        />
        <div style={{ flex: 1 }} />
        <div style={{ display: 'flex', gap: 4 }}>
          {c.badges.map((b) => <AssetBadge key={b} kind={b} />)}
        </div>
        {c.startedAt && (
          <DateCell kind={c.dateKind} text={c.startedAt} style={{ fontSize: 10.5, color: 'var(--muted-2)', whiteSpace: 'nowrap' }} />
        )}
      </div>
    </button>

      {/* Overflow menu — a SIBLING of the card button (a button can't nest inside
          another button). The stopPropagation wrapper keeps menu clicks (open,
          Delete-confirm, Pause, …) from bubbling up and firing the card's open. */}
      {items.length > 0 && (
        <div
          // Logical, not physical: in Hebrew/Arabic the card's identity column
          // is on the right, and a physically-right-pinned menu sat on top of it.
          style={{ position: 'absolute', top: 10, insetInlineEnd: 12, zIndex: 2 }}
          onClick={(e) => e.stopPropagation()}
        >
          <ActionsMenu items={items} />
        </div>
      )}
    </div>
  );
};

// ─── Compact row view (#162) ────────────────────────────────────────────────
// The header and every row share ONE column spec (`ROW_COLUMNS` →
// `ROW_GRID` + per-column `align`, in ./campaign-list-columns) so columns line
// up down the page and a header cell can never drift off its body cell — ragged
// columns are what make a dense list unscannable, which is the whole point of
// this mode.
const ROW_CELL: React.CSSProperties = {
  fontSize: 12, color: 'var(--ink-2)', overflow: 'hidden',
  textOverflow: 'ellipsis', whiteSpace: 'nowrap',
};

const CampaignRowHeader: React.FC = () => (
  <div
    style={{
      display: 'grid', gridTemplateColumns: ROW_GRID, gap: 12, alignItems: 'center',
      padding: '7px 15px', borderBottom: '1px solid var(--line)',
      fontSize: 10.5, fontWeight: 600, letterSpacing: '.04em', textTransform: 'uppercase',
      color: 'var(--muted-2)', background: 'var(--surface-2)',
      borderRadius: '10px 10px 0 0', border: '1px solid var(--line)', borderBottomWidth: 1,
    }}
  >
    {ROW_COLUMNS.map((col) => (
      <span key={col.label} style={{ textAlign: col.align }}>{col.label}</span>
    ))}
  </div>
);

// Module-scope so the reference is stable — a component defined inline in the
// render would remount (and lose scroll position) on every store update.
const RowsHeader: React.FC = () => (
  <div style={{ padding: '16px 24px 0', width: '100%', maxWidth: CARD_COLUMN_MAX, margin: '0 auto', boxSizing: 'border-box' }}>
    <CampaignRowHeader />
  </div>
);

/** Stable Virtuoso `components` objects — see the usage note at the list. */
const ROWS_COMPONENTS = { Header: RowsHeader } as const;
const NO_COMPONENTS = {} as const;


interface CampaignRowProps {
  c: CardModel;
  onOpen: (c: CardModel) => void;
  onEditContent?: (id: string) => void;
  onEditSchedule?: (id: string) => void;
  /** #590: bulk selection (drip rows only). */
  selectable?: boolean;
  selected?: boolean;
  onToggleSelect?: (id: string) => void;
}

// Deliberately NO message preview — that is the card's job and the reason the
// card mode only fits ~3 rows per screen. This mode shows only what actually
// differs between a customer's near-identical campaigns.
const CampaignRow: React.FC<CampaignRowProps> = ({ c, onOpen, onEditContent, onEditSchedule, selectable, selected, onToggleSelect }) => {
  const { t } = useTranslation(namespace);
  // FE#259: give the list row the SAME always-visible actions menu the card has
  // (this resolution mirrors CampaignCard) so a row is no longer a menu blind
  // spot. #162 made the row a pure click-through; Noam asked for the menu here.
  const nav = useCampaignsRedesignNav();
  const isStandalone = c.kind === 'campaign';
  const realDrip = useAppSelector((s) =>
    c.kind === 'drip' ? dripsSelectOneObjectByQuery(s.DBDripReducer, { _id: c.id }) : undefined,
  ) as Drip | undefined;
  const standaloneCampaign = useAppSelector((s) =>
    isStandalone ? campaignsSelectOneObjectByQuery(s.DBCampaignReducer, { _id: c.id }) : undefined,
  ) as Campaign | undefined;
  const effectiveDrip: Drip | undefined = isStandalone
    ? (standaloneCampaign ? { _id: standaloneCampaign._id, name: standaloneCampaign.name } : undefined)
    : (realDrip ? { ...realDrip, status: c.status as DripStatus } : undefined);
  const { items, savingTemplate, stopSaveTemplate, confirmingDelete, confirmDelete, cancelDelete } = useDripActions({
    drip: effectiveDrip, realDrip, standaloneCampaign, dripId: c.id, isStandalone,
    onEdit: (id) => nav?.editCampaign(id), onEditContent, onEditSchedule,
  });

  return (
  <div
    role="button"
    tabIndex={0}
    onClick={() => onOpen(c)}
    onKeyDown={(e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onOpen(c);
      }
    }}
    style={{
      display: 'grid', gridTemplateColumns: ROW_GRID, gap: 12, alignItems: 'center',
      padding: '9px 15px', cursor: 'pointer', background: 'var(--surface)',
      borderLeft: '1px solid var(--line)', borderRight: '1px solid var(--line)',
      borderBottom: '1px solid var(--line)',
    }}
    onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--surface-2)'; }}
    onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--surface)'; }}
  >
    <span style={{ ...ROW_CELL, display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
      {/* #590: bulk-selection checkbox (drip rows only). stopPropagation so a
          toggle click never fires the row's open. */}
      {selectable && (
        <input
          type="checkbox"
          checked={!!selected}
          onClick={(e) => e.stopPropagation()}
          onChange={() => onToggleSelect?.(c.id)}
          aria-label={t('campaignsRedesign.bulk.selectRow', 'Select campaign')}
          style={{ cursor: 'pointer', flexShrink: 0 }}
        />
      )}
      <Megaphone size={13} stroke="var(--muted-2)" style={{ flexShrink: 0 }} />
      <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {c.name || t('campaignsRedesign.untitledCampaign', { defaultValue: 'Untitled campaign' })}
      </span>
      {c.steps > 1 && (
        <span className="pill brand" style={{ fontSize: 9.5, flexShrink: 0 }}>{t('campaignsRedesign.counts.messagesShort', { count: c.steps })}</span>
      )}
    </span>
    <span style={ROW_CELL}>{c.audienceName || '—'}</span>
    <span style={{ ...ROW_CELL, textAlign: alignFor('Contacts') }}>
      {c.audienceCount === undefined ? '—' : c.audienceCount.toLocaleString()}
    </span>
    <span style={{ ...ROW_CELL, textAlign: alignFor('Sent') }}>{c.sent.toLocaleString()}</span>
    <DateCell kind={c.dateKind} text={c.startedAt} style={{ ...ROW_CELL, color: 'var(--muted)' }} />
    <span style={{ minWidth: 0, overflow: 'hidden' }}><StatusLabel status={c.displayStatus ?? c.status} /></span>
    {/* FE#259: the row's own always-visible 3-dot actions menu. stopPropagation
        so opening the menu / confirming a delete never fires the row's open. */}
    <span
      style={{ display: 'flex', justifyContent: 'center' }}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      {items.length > 0 && <ActionsMenu items={items} />}
    </span>
    <SaveAsDripTemplateModal open={savingTemplate} drip={realDrip} onClose={stopSaveTemplate} />
    {confirmingDelete && (
      <MessageConfirmationDialog
        title={t('campaignsRedesign.deleteDialog.title', 'Delete this campaign and stop sending?')}
        description={t('campaignsRedesign.deleteDialog.body', 'Anyone who has not received it yet never will. Messages already sent cannot be recalled.')}
        confirmLabel={t('campaignsRedesign.deleteDialog.confirm', 'Stop and delete')}
        destructive
        onClose={(value?: string) => { if (value !== undefined) confirmDelete(); else cancelDelete(); }}
      />
    )}
  </div>
  );
};

// ─── Filter pills ───────────────────────────────────────────────────────────
// Status-lifecycle filters — All → Running → Completed → Drafts & scheduled.
// (The old 'multi'/"With follow-ups" pill filtered by shape, not status, which
// didn't belong in a status row — a drip's step count is visible on the card.)
type FilterId = 'all' | 'active' | 'completed' | 'draft';
const isFilterId = (s: string | null): s is FilterId =>
  s === 'all' || s === 'active' || s === 'completed' || s === 'draft';

// ─── Management tabs ─────────────────────────────────────────────────────────
type ManagementTab = 'audiences' | 'templates' | 'campaigns';
// Labels are i18n KEYS resolved at render, not literals: this list is
// module-level, so a literal here is frozen at import time and can never be
// translated even once the locale files carry the strings.
const TABS: ReadonlyArray<{ id: ManagementTab; labelKey: string; fallback: string }> = [
  { id: 'campaigns', labelKey: 'campaignsRedesign.tabs.campaigns', fallback: 'Campaigns' },
  { id: 'audiences', labelKey: 'campaignsRedesign.tabs.audiences', fallback: 'Audiences' },
  { id: 'templates', labelKey: 'campaignsRedesign.tabs.templates', fallback: 'Templates' },
];
const isManagementTab = (s: string | null): s is ManagementTab =>
  s === 'audiences' || s === 'templates' || s === 'campaigns';

// A standalone campaign's status counts as "running" for the Running pill when
// getCampaignRealStatus maps it to a live state.
const RUNNING_STATUSES = new Set(['active', 'running']);
const DRAFT_STATUSES = new Set(['draft', 'pending']);
// A drip whose every step completed, or a standalone campaign mapped to
// 'completed' by campaignCardStatus, reads as Completed. 'expired' (FE#256 — a
// non-recurring campaign whose run ended but was never advanced) is a terminal,
// finished state too, so it lives under the Completed pill (and, crucially, NOT
// under Running).
const COMPLETED_STATUSES = new Set(['completed', 'expired']);

// Max width of the campaigns list content column (filter pills + cards). Keeps
// the list a contained, centered column with comfortable side gutters on wide
// viewports — matching the Audiences / logs panels — instead of edge-to-edge.
const CARD_COLUMN_MAX = 1040;

const CAMPAIGN_SORT_STORAGE_KEY = 'bt:campaigns:sort';
const CAMPAIGN_VIEW_STORAGE_KEY = 'bt:campaigns:view';

// ─── CampaignsList ────────────────────────────────────────────────────────
const CampaignsList: React.FC = () => {
  const { t } = useTranslation(namespace);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // The filter pill lives in the URL (`?filter=`) too, so reload + back/forward
  // restore the exact list state — a UI change should be reflected in the URL.
  const filter: FilterId = isFilterId(searchParams.get('filter'))
    ? (searchParams.get('filter') as FilterId)
    : 'all';
  const setFilter = React.useCallback((next: FilterId) => {
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      if (next === 'all') p.delete('filter'); // default — keep the URL clean
      else p.set('filter', next);
      return p;
    });
  }, [setSearchParams]);
  // FE#339 — the audience/board filter also lives in the URL (`?audience=`), so
  // reload + back/forward restore the isolated view. `ALL_AUDIENCES_ID` is the
  // default and is kept OUT of the URL (clean links). The raw param is coerced
  // against the loaded options below, so a stale/removed board id degrades to All.
  const audienceParam = searchParams.get('audience');
  const setAudience = React.useCallback((next: string) => {
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      if (next === ALL_AUDIENCES_ID) p.delete('audience');
      else p.set('audience', next);
      return p;
    });
  }, [setSearchParams]);
  // Active tab lives in the URL (`?tab=`) so reload + back/forward restore it.
  const tab: ManagementTab = isManagementTab(searchParams.get('tab'))
    ? (searchParams.get('tab') as ManagementTab)
    : 'campaigns';
  const setTab = React.useCallback((next: ManagementTab) => {
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      // 'campaigns' is the default — keep the URL clean by omitting it.
      if (next === 'campaigns') p.delete('tab');
      else p.set('tab', next);
      return p;
    });
  }, [setSearchParams]);

  // Fetch on mount: the user's drips + standalone campaigns (no dripId) +
  // (separately) the boards backing the contact-count + name selectors.
  useEffect(() => {
    // $limit/$sort explicit: the service default paginates to 10, oldest-first,
    // so newly-created drips (id/createdAt at the tail) never made it into the
    // page — a reload wouldn't show them. Fetch newest-first, well above any
    // realistic drip count.
    dispatch(DBDripThunks.find({ deleted: { $ne: true }, $sort: { createdAt: -1 }, $limit: 500 }));
    // Existing standalone campaigns. Do NOT pass `dripId: null` in the query:
    // it serializes to `dripId=` (empty string) over REST and the backend then
    // matches `dripId: ''`, which matches nothing (real campaigns have the field
    // ABSENT, not empty). The campaigns service's default hook already injects
    // `dripId: null` (matching absent-or-null in Mongo) to hide drip-STEP
    // campaigns, so omitting it returns exactly the standalone campaigns; the
    // `!c.dripId` selector filter below keeps them.
    dispatch(DBCampaignThunks.find({ deleted: { $ne: true }, $sort: { createdAt: -1 }, $limit: 500 }));
  }, [dispatch]);

  const drips = useAppSelector((state) =>
    dripsSelectByQuery(state.DBDripReducer, { deleted: { $ne: true } }, [], { updatedAt: -1 }),
  );

  // First-load flags for the two collections the campaigns list merges. Until
  // BOTH have resolved once, a cold reload would flash "No campaigns yet" before
  // the cards arrive — so we show the skeleton until then.
  const dripsLoaded = useAppSelector((state) => state.DBDripReducer.itemsLoaded);
  const campaignsLoaded = useAppSelector((state) => state.DBCampaignReducer.itemsLoaded);
  const listLoaded = dripsLoaded && campaignsLoaded;

  // Step-campaigns are real campaigns tagged with the drip's id. Fetch all in
  // one query keyed on the visible drips, then aggregate `sentCount` per drip.
  const dripIds = useMemo(
    () => drips.map((d) => String(d._id ?? '')).filter(Boolean),
    [drips],
  );

  // Key the effect on the id CONTENT (not the array reference): `dripsSelectByQuery`
  // can return a fresh array on every store update, and depending on the array
  // reference re-fired this fetch on a loop (the repeated `campaigns/?dripId[$in]`
  // requests). Split into ≤20-id batches so the `$in` query never trips the
  // backend's arrayLimit (see chunkIds).
  const dripIdsKey = dripIds.join(',');
  useEffect(() => {
    if (dripIds.length === 0) return;
    for (const batch of chunkIds(dripIds)) {
      // `$limit: 500` matches the two finds above. WITHOUT it the backend's
      // default page size (10) applies PER BATCH of up to 20 drips, so most
      // step campaigns never reach the store: `sentCount` under-reports,
      // `deriveDripStatus` reads a truncated step set, and — since #103 —
      // `useDripActions` resolves no current-step campaign, so the card's
      // "Edit"/"Edit message" item silently does not render at all.
      dispatch(DBCampaignThunks.find({ dripId: { $in: batch }, $limit: 500 }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, dripIdsKey]);

  const stepCampaigns = useAppSelector((state) =>
    campaignsSelectByQuery(state.DBCampaignReducer, { dripId: { $in: dripIds } }),
  );

  // Step-campaigns grouped by their drip id — the input the shared
  // `deriveDripStatus` selector needs so a card's status matches the detail's
  // (a drip whose every step campaign is done reads "Completed" even if the
  // stored `drip.status` lags).
  const stepCampaignsByDripId = useMemo(() => {
    const acc: Record<string, Campaign[]> = {};
    for (const campaign of stepCampaigns) {
      const id = campaign.dripId ? String(campaign.dripId) : '';
      if (!id) continue;
      (acc[id] ??= []).push(campaign);
    }
    return acc;
  }, [stepCampaigns]);

  const taskSumByBoardIdRecord = useAppSelector(getTaskSumByBoardIdRecord);
  const boardNameByBoardIdRecord = useAppSelector(getBoardNameByBoardIdRecord);

  // Existing standalone campaigns — real, user-created campaigns that aren't
  // drip STEPS. The product model treats each as a 1-step drip.
  //
  // A standalone campaign is one with NO drip step id: existing campaigns omit
  // `dripId` entirely (the field is absent/`undefined`), while drip-step
  // campaigns store a real id. We must include BOTH "field absent" and
  // "field === null". The DB selector (sift) is fetched with `{dripId: null}`,
  // and the backend `find` matches missing-or-null in Mongo, so the store holds
  // them — but to be robust against any selector/normalisation that treats an
  // absent field differently from an explicit `null`, we select the non-deleted
  // set broadly and filter out drip steps in JS with `!c.dripId` (true for both
  // `undefined` and `null`, false for a real step id). This guarantees an
  // account with standalone campaigns sees them as single-message cards.
  const nonDeletedCampaigns = useAppSelector((state) =>
    campaignsSelectByQuery(state.DBCampaignReducer, { deleted: { $ne: true } }),
  );
  const standaloneCampaigns = useMemo(
    () => nonDeletedCampaigns.filter((c) => !c.dripId),
    [nonDeletedCampaigns],
  );

  // ─── Virtual-scroll pagination ────────────────────────────────────────────
  // Pagination is INTENTIONAL: we never fetch all campaigns up front (the store
  // would balloon and the initial `find` would hammer the backend). The mount
  // `find` above loads the first page; the grid is virtualized (react-virtuoso,
  // the same primitive `LazyLoadingVirtuoso` wraps for the bulk dashboard) so
  // only on-screen cards mount, and `endReached` fetches the NEXT page on scroll.
  //
  // `total` is the server-reported count from the last campaigns `find`; with no
  // drips it is the standalone count, so we stop paginating once the loaded
  // standalone set reaches it. `$skip` = the number of standalone campaigns
  // already in the store (mirrors `LazyLoadingVirtuoso.loadMore`, which skips by
  // the current loaded length). A ref guards against duplicate in-flight fetches
  // for the same skip offset (virtuoso can fire `endReached` repeatedly).
  const campaignsTotal = useAppSelector((state) => state.DBCampaignReducer.total ?? 0);
  const lastRequestedSkipRef = React.useRef<number>(-1);

  const loadMoreCampaigns = React.useCallback(() => {
    const loaded = standaloneCampaigns.length;
    // Nothing more to fetch (or counts not yet known) — bail.
    if (campaignsTotal > 0 && loaded >= campaignsTotal) return;
    // Already requested this page — don't double-fire.
    if (lastRequestedSkipRef.current === loaded) return;
    lastRequestedSkipRef.current = loaded;
    dispatch(
      DBCampaignThunks.find({
        deleted: { $ne: true },
        // FE#339 — page in the SAME order the mount `find` used (`createdAt: -1`).
        // `$skip: loaded` only lands on the next unseen page if every page shares
        // one server order; the previous `updatedAt: -1` here disagreed with the
        // mount's `createdAt: -1`, so re-touched campaigns reshuffled between pages
        // and some rows were skipped forever — a heavy account then literally could
        // not scroll down to today. Consistent ordering makes the offset monotonic.
        $sort: { createdAt: -1 },
        $skip: loaded,
      }),
    );
  }, [dispatch, standaloneCampaigns.length, campaignsTotal]);

  // Hide empty drafts (no audience + no real step content) from EVERY view —
  // filtering the source drip list propagates to All + every filter pill and
  // the counts below. Real drafts (with content) survive and still render.
  const visibleDrips = useMemo(() => drips.filter((d) => !isEmptyDraft(d)), [drips]);

  // ── Audience contact counts for the Contacts stat ──────────────────────────
  // A standalone campaign carries its own denormalised `contactCount` (the
  // audience size at send), so the Contacts stat reads straight off it for the
  // vast majority. The exceptions — a campaign with no `contactCount` yet, and
  // every drip (the drip doc has none) — fall back to the LIVE audience board
  // count via `getTaskSumByBoardIdRecord`. That selector only knows boards that
  // are LOADED (keyed on `board.tasksCount ?? loaded-task-count`), and this list
  // doesn't otherwise fetch audience boards — so load the board + its contacts
  // for exactly those boardIds. The set is tiny (audiences are reused, and the
  // contactCount-bearing majority is skipped), so this stays cheap.
  const boardIdsNeedingCount = useMemo(() => {
    const ids = new Set<string>();
    for (const d of visibleDrips) {
      for (const b of toAudienceIds(d.boardIDArray)) ids.add(b);
    }
    for (const c of standaloneCampaigns) {
      if (typeof c.contactCount === 'number' && c.contactCount > 0) continue;
      for (const b of toAudienceIds(c.boardIDArray)) ids.add(b);
    }
    return Array.from(ids);
  }, [visibleDrips, standaloneCampaigns]);
  const boardIdsKey = boardIdsNeedingCount.join(',');
  // Only audiences not already asked for. The list arrives in two phases (drips,
  // then campaigns), so the id set GROWS; re-sending the whole set each time
  // re-downloaded every row of the audiences from the first phase — measured on
  // the list's first load: boards and tasks for 3 audiences, then again for those
  // same 3 plus 2 more.
  const requestedBoardIdsRef = React.useRef<Set<string>>(new Set());
  useEffect(() => {
    const fresh = boardIdsNeedingCount.filter((id) => !requestedBoardIdsRef.current.has(id));
    if (fresh.length === 0) return;
    fresh.forEach((id) => requestedBoardIdsRef.current.add(id));
    for (const batch of chunkIds(fresh)) {
      dispatch(DBBoardsThunks.find({ _id: { $in: batch } }));
      dispatch(DBTasksThunks.find({ boardId: { $in: batch }, deleted: false, archived: { $ne: true }, $paginate: false }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, boardIdsKey]);

  const dripCards = useMemo<CardModel[]>(() => {
    return visibleDrips.map((drip, idx) => {
      const id = String(drip._id ?? '');
      const steps = drip.steps?.length ?? 1;
      const currentStep = drip.currentStep ?? 0;
      const status = deriveDripStatus(drip, stepCampaignsByDripId[id] ?? []);
      // Display badge: a future-dated, not-yet-started drip reads "Scheduled", not
      // "Running · 0 sent". Logical `status` above is unchanged (glyph + filter read it).
      const displayStatus = deriveDripDisplayStatus(drip, stepCampaignsByDripId[id] ?? []);
      const stopOnReply = drip.stopOnReply ?? false;
      const recurring = steps > 1 && !stopOnReply;

      // A drip can target several audiences (#161) — label and count ALL of them,
      // or the card under-reports the campaign's reach.
      const boardIds = toAudienceIds(drip.boardIDArray);
      const audienceName =
        deriveAudienceLabel(
          boardIds.map((b) => boardNameByBoardIdRecord[b] ?? t('audiences.unnamed', 'Untitled audience')),
          t,
        ) ?? t('campaignsRedesign.noAudienceChosen', 'No audience chosen');
      const audienceCount = boardIds.length
        ? boardIds.reduce((n, b) => n + (taskSumByBoardIdRecord[b] ?? 0), 0)
        : undefined;

      // "Sent" = messages dispatched across the sequence. When the per-step
      // campaigns are loaded (running drips), sum each dispatched step's real
      // recipient count — the SAME per-message rule the standalone card uses,
      // which also captures stop-on-reply drop-off exactly. Older completed drips
      // keep NO step campaigns, so fall back to an estimate from the drip doc:
      // (dispatched step count) × audience, where a completed drip dispatched
      // every step and a running one dispatched the steps BEFORE its currentStep.
      const dripBoardCount = audienceCount ?? 0;
      const stepCs = stepCampaignsByDripId[id] ?? [];
      const doneSteps = status === 'completed'
        ? steps
        : status === 'draft'
          ? 0
          : Math.max(0, currentStep); // active / paused / aborted
      const sent = stepCs.length > 0
        ? stepCs.reduce((sum, sc) => sum + sentForCampaign(sc, dripBoardCount), 0)
        : doneSteps * dripBoardCount;

      // FE#340 — resolve WHICH date the row shows and WHAT it means. A running
      // drip's next not-yet-sent step (FE#341) is the upcoming send; a completed
      // drip shows its real start/sent time, not the launch/created fallback.
      const dripListDate = resolveListDate({
        status,
        hasDispatched: sent > 0 || status === 'completed',
        upcomingSendAt: nextUpcomingDripSendAt(drip, stepCs, new Date()),
        startedAt: drip.startedAt,
        scheduledAt: drip.schedule?.dueDate,
        completedAt: drip.updatedAt,
        createdAt: drip.createdAt,
      });

      // Gap configured on the current step = the wait before the next follow-up.
      const nextStepGap =
        steps > 1 && currentStep < steps - 1
          ? dripGapText(drip.steps?.[currentStep]?.gapAfter)
          : '';

      return {
        id,
        kind: 'drip' as const,
        name: drip.name ?? t('campaignsRedesign.untitledCampaign', { defaultValue: 'Untitled campaign' }),
        audienceName,
        audienceCount: audienceCount === -1 ? undefined : audienceCount,
        audienceColor: AUD_COLORS[idx % AUD_COLORS.length],
        boardIds,
        steps,
        currentStep,
        status,
        displayStatus,
        stopOnReply,
        recurring,
        sent,
        // No campaign-level reply count is available in a list context.
        replied: undefined,
        nextStepGap,
        badges: badgesForSteps(drip.steps),
        // Preview the first step's message (the entry point of the sequence).
        previewMessage: templateToPreviewMessage(drip.steps?.[0]?.template),
        hasTemplate: !!drip.steps?.[0]?.template,
        // Search across the whole sequence — every step's message, not just
        // the previewed first one.
        searchText: [
          drip.name ?? '',
          ...(drip.steps ?? []).flatMap((s) => [s.template?.name ?? '', s.template?.message ?? '']),
        ]
          .join('\n')
          .toLowerCase(),
        // FE#340/#341 — the resolved date + its meaning (Sent/Scheduled/Started/…).
        startedAt: dripListDate.at ? formatStartedRaw(dripListDate.at) : '',
        dateKind: dripListDate.kind,
        createdAtMs: toMs(drip.createdAt ?? drip.startedAt),
        // Sort by the SAME resolved date the label shows.
        scheduledAtMs: dripListDate.at?.getTime() ?? toMs(drip.createdAt ?? drip.startedAt),
      };
    });
  }, [visibleDrips, stepCampaignsByDripId, taskSumByBoardIdRecord, boardNameByBoardIdRecord, t]);

  // Map each standalone campaign → the same card shape, as a single-message
  // (steps=1) card. "Sent" is derived via `sentForCampaign` (dispatched ?
  // contactCount : 0) — the campaign doc has no `sentCount`. There is NO
  // campaign-level reply count without loading per-message acks (a list view
  // deliberately doesn't), so Replied stays `—`. Contacts come from the
  // campaign's own contactCount, or the board task-sum when absent.
  const campaignCards = useMemo<CardModel[]>(() => {
    return standaloneCampaigns.map((campaign, idx) => {
      const id = String(campaign._id ?? '');
      const boardIds = toAudienceIds(campaign.boardIDArray);
      const audienceName =
        deriveAudienceLabel(
          boardIds.map((b) => boardNameByBoardIdRecord[b] ?? t('audiences.unnamed', 'Untitled audience')),
          t,
        ) ?? t('campaignsRedesign.noAudienceChosen', 'No audience chosen');
      const boardCount = boardIds.length
        ? boardIds.reduce((n, b) => n + (taskSumByBoardIdRecord[b] ?? 0), 0)
        : undefined;
      const audienceCount =
        typeof campaign.contactCount === 'number' && campaign.contactCount > 0
          ? campaign.contactCount
          : boardCount === -1
            ? undefined
            : boardCount;

      const campaignStatus = campaignCardStatus(campaign);
      const campaignSent = sentForCampaign(campaign, audienceCount ?? 0);
      // FE#340 — a COMPLETED campaign shows when it actually SENT (`startedAt`),
      // not its scheduled/created time; a pending one shows the scheduled due date.
      const campaignListDate = resolveListDate({
        status: campaignStatus,
        hasDispatched: campaignSent > 0 || campaignStatus === 'completed',
        startedAt: campaign.startedAt,
        scheduledAt: campaign.schedule?.dueDate,
        completedAt: campaign.updatedAt,
        createdAt: campaign.createdAt,
      });

      return {
        id,
        kind: 'campaign' as const,
        // The CAMPAIGN's own name is the identity shown in the detail — prefer it
        // over the template's name (an API campaign's template may be named "API"
        // while the campaign is "msw-…"), so the card and detail agree.
        name: campaign.name ?? campaign.template?.name ?? t('campaignsRedesign.untitledCampaign', { defaultValue: 'Untitled campaign' }),
        audienceName,
        audienceCount,
        // Offset the colour ring so standalone cards don't all share the drips' hue.
        audienceColor: AUD_COLORS[(idx + visibleDrips.length) % AUD_COLORS.length],
        boardIds,
        steps: 1,
        currentStep: 0,
        status: campaignStatus,
        // A standalone campaign already reads its own status (pending → "Scheduled");
        // display == logical here.
        displayStatus: campaignStatus,
        stopOnReply: false,
        recurring: false,
        // Dispatched → sent to its whole audience (contactCount, or the board
        // fallback the Contacts stat also uses); otherwise 0.
        sent: campaignSent,
        // No campaign-level reply count is available in a list context.
        replied: undefined,
        nextStepGap: '',
        badges: badgesForTemplate(campaign.template),
        previewMessage: templateToPreviewMessage(campaign.template),
        hasTemplate: !!campaign.template,
        searchText: [campaign.name ?? '', campaign.template?.name ?? '', campaign.template?.message ?? '']
          .join('\n')
          .toLowerCase(),
        startedAt: campaignListDate.at ? formatStartedRaw(campaignListDate.at) : '',
        dateKind: campaignListDate.kind,
        createdAtMs: toMs(campaign.createdAt),
        // Sort by the SAME resolved date the label shows.
        scheduledAtMs: campaignListDate.at?.getTime() ?? toMs(campaign.createdAt),
      };
    });
  }, [standaloneCampaigns, visibleDrips.length, taskSumByBoardIdRecord, boardNameByBoardIdRecord, t]);

  // Unified, newest-first grid across drips + standalone campaigns.
  const cards = useMemo<CardModel[]>(
    () => [...dripCards, ...campaignCards].sort((a, b) => b.createdAtMs - a.createdAtMs),
    [dripCards, campaignCards],
  );

  const counts = useMemo(
    () => ({
      all: cards.length,
      active: cards.filter((c) => RUNNING_STATUSES.has(c.status)).length,
      completed: cards.filter((c) => COMPLETED_STATUSES.has(c.status)).length,
      draft: cards.filter((c) => DRAFT_STATUSES.has(c.status)).length,
    }),
    [cards],
  );

  // FE#339 — audience/board filter options, built from the loaded cards' boards
  // (named via the same board-name record the cards use). The raw URL param is
  // coerced against these so a since-removed board falls back to "All audiences".
  const audienceOptions = useMemo(
    () => collectAudienceOptions(t, cards, boardNameByBoardIdRecord),
    [t, cards, boardNameByBoardIdRecord],
  );
  const activeAudience = coerceAudienceId(audienceParam, audienceOptions);

  // Free-text search over name + every message the campaign sends (all drip
  // steps included via each card's precomputed `searchText` haystack).
  const [search, setSearch] = React.useState('');

  // #103: the launched-campaign message editor, opened from a card's overflow
  // menu. Held HERE rather than in `CampaignCard` because the cards are
  // virtualized — a modal owned by a row would be torn down as soon as that row
  // scrolled out of the viewport, and this modal's unmount fires the abort that
  // resumes the campaign.
  const [editingCampaignId, setEditingCampaignId] = React.useState<string | null>(null);

  // Date ordering. Persisted per-device: it's a display preference, not a
  // business rule, so localStorage is the right home (and a lost value just
  // falls back to the previous hard-coded default — newest first).
  const [sort, setSort] = React.useState<CampaignSortId>(() => {
    try {
      return coerceCampaignSort(window.localStorage.getItem(CAMPAIGN_SORT_STORAGE_KEY));
    } catch {
      return DEFAULT_CAMPAIGN_SORT; // private mode / storage disabled
    }
  });
  React.useEffect(() => {
    try {
      window.localStorage.setItem(CAMPAIGN_SORT_STORAGE_KEY, sort);
    } catch {
      /* non-fatal — the sort still applies for this session */
    }
  }, [sort]);

  // Density (#162), remembered the same way.
  const [view, setView] = React.useState<CampaignViewMode>(() => {
    try {
      return coerceCampaignView(window.localStorage.getItem(CAMPAIGN_VIEW_STORAGE_KEY));
    } catch {
      return DEFAULT_CAMPAIGN_VIEW;
    }
  });
  // Mobile forces the card layout — the compact 'rows' grid (fixed ~482px) can't
  // fit a phone. The stored preference is kept for when they're back on desktop.
  const isMobile = useIsMobile();
  const effectiveView: CampaignViewMode = effectiveCampaignView(isMobile, view);
  React.useEffect(() => {
    try {
      window.localStorage.setItem(CAMPAIGN_VIEW_STORAGE_KEY, view);
    } catch {
      /* non-fatal */
    }
  }, [view]);

  const shown = useMemo(() => {
    let list = cards;
    if (filter === 'active') list = list.filter((c) => RUNNING_STATUSES.has(c.status));
    else if (filter === 'completed') list = list.filter((c) => COMPLETED_STATUSES.has(c.status));
    else if (filter === 'draft') list = list.filter((c) => DRAFT_STATUSES.has(c.status));
    // FE#339 — dedicated audience/board filter (distinct from the free-text search).
    if (activeAudience !== ALL_AUDIENCES_ID) {
      list = list.filter((c) => cardMatchesAudience(c, activeAudience));
    }
    const q = search.trim().toLowerCase();
    if (q) list = list.filter((c) => c.searchText.includes(q));
    return sortCampaignCards(list, sort);
  }, [cards, filter, search, sort, activeAudience]);

  // #590: bulk pause/stop. Selection is keyed by drip id; only DRIP cards are
  // selectable because the bulk mutations (pause-drip / abort-drip) take a drip
  // id — a standalone campaign has no drip to pause/abort. The pure collector
  // (useBulkDripActions) awaits each op and reports the ok/failed split so the
  // toolbar can say "N stopped, M failed" instead of silently swallowing a
  // campaign that failed to stop.
  const bulk = useBulkDripActions();
  const [selectedIds, setSelectedIds] = React.useState<ReadonlySet<string>>(() => new Set());
  const [bulkConfirm, setBulkConfirm] = React.useState<null | 'pause' | 'stop'>(null);
  const [bulkBusy, setBulkBusy] = React.useState(false);
  const [bulkNotice, setBulkNotice] = React.useState<string | null>(null);

  const selectableIds = useMemo(
    () => shown.filter((c) => c.kind === 'drip').map((c) => c.id),
    [shown],
  );
  const selectedCount = useMemo(
    () => selectableIds.filter((id) => selectedIds.has(id)).length,
    [selectableIds, selectedIds],
  );
  const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selectedIds.has(id));

  const toggleSelect = React.useCallback((id: string) => {
    setBulkNotice(null);
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);
  const toggleSelectAll = React.useCallback(() => {
    setBulkNotice(null);
    setSelectedIds((prev) => {
      const all = selectableIds.length > 0 && selectableIds.every((id) => prev.has(id));
      return all ? new Set<string>() : new Set(selectableIds);
    });
  }, [selectableIds]);
  const clearSelection = React.useCallback(() => setSelectedIds(new Set<string>()), []);

  const runBulk = async (kind: 'pause' | 'stop') => {
    const ids = selectableIds.filter((id) => selectedIds.has(id));
    setBulkConfirm(null);
    if (ids.length === 0) return;
    setBulkBusy(true);
    const res = kind === 'stop' ? await bulk.stopMany(ids) : await bulk.pauseMany(ids);
    setBulkBusy(false);
    clearSelection();
    setBulkNotice(t('campaignsRedesign.bulk.result', '{{ok}} updated, {{failed}} failed', {
      ok: res.ok.length,
      failed: res.failed.length,
    }));
  };

  const onNew = () => navigate(flowPath('/campaigns/new'));
  // FE#331 — start a new drip from a saved template (steps + gaps reused).
  const onFromTemplate = () => navigate(flowPath('/campaigns/from-template'));
  // Every campaign — drip OR standalone — opens the unified SequenceDetail at
  // /campaigns/:id. A drip renders from its real doc; a standalone campaign is
  // synthesized into a 1-step sequence there, so both share one detail (header +
  // actions + progression + per-step logs + summary).
  const onOpen = (card: CardModel) => {
    navigate(`/campaigns/${card.id}`);
  };
  // "Edit campaign" (a scheduled standalone campaign) opens the same page: its
  // audience and send time are the page's Audience and When tabs.
  const openCampaignPage = (id: string) => navigate(`/campaigns/${id}`);

  // First-run: the account has NO campaigns at all (not "none match the
  // filters"). The whole toolbar — search, status pills, audience filter, sort,
  // density, From template — is hidden in that case: there is nothing to
  // search, filter or sort, and every control is one more thing between a new
  // user and the single button they should press. `listLoaded` keeps the
  // skeleton on until both collections have resolved once, so this never
  // flashes on a cold reload.
  const isFirstRun = listLoaded && cards.length === 0;

  const pills: Array<[FilterId, string, number]> = [
    ['all', t('campaignsRedesign.filter.all', 'All'), counts.all],
    ['active', t('campaignsRedesign.filter.running', 'Running'), counts.active],
    ['completed', t('campaignsRedesign.filter.completed', 'Completed'), counts.completed],
    // "Drafts & scheduled" fuses two internal statuses and names both of them
    // in the model's words. What the bucket actually holds is everything that
    // has not gone out yet.
    ['draft', t('campaignsRedesign.filter.notSentYet', 'Not sent yet'), counts.draft],
  ];

  return (
    <div className="col" style={{ flex: 1, background: 'var(--bg)' }}>
      {/* header — no back button here: the redesign overlay already renders a
          floating close (X) top-right (see campaigns-redesign-view.tsx).
          On mobile the app shell drops a FIXED nav burger at top-left
          (app-view.tsx `!showHeader`: position:fixed, insetInlineStart:8,
          z-index:1100); indent the title block's leading edge to clear it. */}
      <div style={{ padding: '24px 24px 0', paddingInlineStart: isMobile ? 56 : 24 }}>
        <h1 style={{ fontSize: 20, fontWeight: 600, letterSpacing: '-0.01em' }}>
          {t('campaignsRedesign.tabs.campaigns', 'Campaigns')}
          {!isFirstRun && <GuideVideoButton lesson="first-campaign" variant="icon" surface="campaigns_header" style={{ marginInlineStart: 8 }} />}
        </h1>
        {/* The subtitle used to define the noun — "Bulk messages and follow-up
            sequences on WhatsApp" — on every visit, forever. A definition helps
            exactly once: on the visit where there is nothing else on the page.
            After that it is a permanent band of text above the thing the person
            actually came for, and the status counts one row below say far more.
            So it is shown on the empty state and nowhere else. */}
        {isFirstRun && (
          <p style={{ fontSize: 12.5, color: 'var(--muted)', marginTop: 2 }}>
            {t('campaignsRedesign.subtitle', 'Bulk messages and follow-up sequences on WhatsApp')}
          </p>
        )}
      </div>

      {/* tabs (management chrome: Audiences / Templates / Campaigns) */}
      <div style={{ padding: '14px 24px 0', display: 'flex', alignItems: 'center', gap: 18, borderBottom: '1px solid var(--line)' }}>
        {TABS.map(({ id, labelKey, fallback }) => {
          const isActive = tab === id;
          const label = t(labelKey, fallback);
          return (
            <button
              key={id}
              type="button"
              onClick={() => setTab(id)}
              style={{
                appearance: 'none', border: 0, background: 'transparent', cursor: 'pointer',
                padding: '0 0 12px', fontSize: 13, fontWeight: isActive ? 600 : 500,
                color: isActive ? 'var(--ink)' : 'var(--muted)',
                borderBottom: isActive ? '2px solid var(--bt-blue)' : '2px solid transparent', marginBottom: -1,
              }}
            >
              {label}
            </button>
          );
        })}
      </div>

      {/* Audiences / Templates tabs render clean redesign list shells
          (AudienceList / TemplateList) over the respective stores. Row actions
          delegate EDITING to the real editors via the redesign nav routes
          (openBoard / openTemplate) — picking is not editing. */}
      {tab === 'audiences' && (
        <div className="scroll" style={{ flex: 1, padding: '8px 0 24px' }}>
          <AudienceList />
        </div>
      )}

      {tab === 'templates' && (
        <div className="scroll" style={{ flex: 1, padding: '8px 0 24px' }}>
          <TemplateList />
        </div>
      )}

      {tab === 'campaigns' && (
        <>
      {/* In-tab toolbar — TWO rows with one job each, because mixing them read
          as "thrown together": the old single flex row put search + status
          pills on the left and let sort/density/actions wrap to a right-aligned
          second line, so at common widths half the controls hugged the left
          edge and half the right, with nothing saying which was which.

            row 1 — FIND + CREATE : search box (left) · From template + New
                                    campaign (right, the page's primary actions)
            row 2 — NARROW + VIEW : status pills (left) · audience filter, sort,
                                    density (right, each with its own label)

          Both rows share the content column, so every control lines up with the
          cards below. The search matches the campaign name OR any message the
          campaign sends (every drip step, via the cards' searchText haystack). */}
      {!isFirstRun && (
      <div style={{ padding: '16px 24px 0', width: '100%', maxWidth: CARD_COLUMN_MAX, margin: '0 auto', boxSizing: 'border-box' }}>
        {/* ── row 1: find + create ── */}
        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
          {/* Grows into whatever the action buttons leave, capped at 320 so it
              never becomes a giant empty field on a wide window — and shrinks
              instead of pushing the buttons onto their own half-empty line. */}
          <div style={{ position: 'relative', flex: isMobile ? '1 1 100%' : '1 1 180px', minWidth: 160, maxWidth: isMobile ? undefined : 320 }}>
            <Search
              size={14}
              style={{ position: 'absolute', insetInlineStart: 11, top: '50%', transform: 'translateY(-50%)', color: 'var(--muted-2)', pointerEvents: 'none' }}
            />
            <input
              className="inp"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('campaignsRedesign.searchPlaceholder', { defaultValue: 'Search campaigns' })}
              style={{ paddingInlineStart: 32, width: '100%' }}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginInlineStart: isMobile ? 0 : 'auto', width: isMobile ? '100%' : undefined }}>
            <button type="button" className="btn ghost" onClick={onFromTemplate} style={{ flex: isMobile ? '1 1 0' : undefined, justifyContent: 'center' }}>
              {/* Same key as the empty state's secondary action below, which
                  passed a DIFFERENT default ("Start from a template"). Once the
                  key is translated one of the two silently changes wording; until
                  then the same control reads differently in two places. One
                  default, both call sites. */}
              <Bookmark size={14} /> {t('blueticksPanel.dripTemplates.fromTemplate', 'Start from a template')}
            </button>
            <button type="button" className="btn primary" onClick={onNew} style={{ flex: isMobile ? '1 1 0' : undefined, justifyContent: 'center' }}>
              <Plus size={14} /> {t('campaignsRedesign.newCampaign', 'New campaign')}
            </button>
          </div>
        </div>

        {/* ── row 2: narrow + view ──
            FE#215: the status pills WRAP rather than sitting in an inner
            overflow-x scroller — on the narrow extension side-panel the last
            label used to clip ("Drafts & sc…"). */}
        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginTop: 12 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', minWidth: 0 }}>
            {pills.map(([id, label, n]) => (
              <button
                key={id}
                type="button"
                onClick={() => setFilter(id)}
                aria-pressed={filter === id}
                style={{
                  appearance: 'none', cursor: 'pointer', padding: '5px 11px', borderRadius: 999,
                  background: filter === id ? 'var(--surface)' : 'transparent',
                  border: filter === id ? '1px solid var(--line-strong)' : '1px solid transparent',
                  color: filter === id ? 'var(--ink)' : 'var(--muted)', fontSize: 12, fontWeight: 500,
                  display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap', flexShrink: 0,
                  boxShadow: filter === id ? '0 1px 2px rgba(11,18,32,.05)' : 'none',
                }}
              >
                {label} <span className="num" style={{ fontSize: 10.5, color: 'var(--muted-2)' }}>{n}</span>
              </button>
            ))}
          </div>

          {/* The view controls travel as ONE group (audience → sort →
              density) and stay LEFT-aligned with the pills. Deliberately not
              pushed right with marginLeft:auto: this group is ~600px wide, so
              on a normal window it wraps to its own line — and a right-aligned
              wrapped line is exactly the "half left, half right" mess this
              toolbar had. Left-aligned, a wrap just becomes a tidy second line
              under the pills. */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, width: isMobile ? '100%' : undefined, flexWrap: 'wrap' }}>
            {/* FE#339 — dedicated audience/board filter (distinct from the
                free-text search): a native select so every board is
                DISCOVERABLE in one open and picking one is exact (two boards
                sharing a word can't collide). Hidden when there are no boards
                to pick — nothing to filter. Paired with the date sort, this is
                what lets an account with hundreds of campaigns/day reach a
                given board's rows without scrolling past every other audience.
                No visible "Audience" caption: the closed select already reads
                "All audiences (382)", or the picked board's own name, so the
                word only said it twice — the a11y name lives on aria-label. */}
            {audienceOptions.length > 0 && (
                <select
                  value={activeAudience}
                  onChange={(e) => setAudience(e.target.value)}
                  className="rd-select"
                  aria-label={t('campaignsRedesign.filterByAudience', 'Filter campaigns by audience')}
                  style={{
                    // `appearance: none` + our own chevron: the NATIVE arrow is
                    // painted hard against the border box, which on a pill
                    // (radius 999) sits inside the curve and reads as a
                    // rendering glitch next to the Sort/View pills. Drawing it
                    // ourselves lets the right padding (28px) actually clear it.
                    appearance: 'none', WebkitAppearance: 'none', MozAppearance: 'none',
                    cursor: 'pointer', borderRadius: 999,
                    // Vertical padding + lineHeight match the sibling pill
                    // buttons (5px / 11px, 12px text) so the three controls sit
                    // on one optical baseline instead of the select running tall.
                    // Logical: physical 28px-right padding put the reserved
                    // chevron gutter on the wrong side of an RTL select, so the
                    // arrow overlapped the option text. The chevron's own
                    // background-position mirrors in CSS (`.rd-select`).
                    paddingBlock: 6, paddingInline: '12px 28px', lineHeight: '16px', boxSizing: 'border-box',
                    backgroundImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'/></svg>")`,
                    backgroundRepeat: 'no-repeat',
                    backgroundColor: activeAudience === ALL_AUDIENCES_ID ? 'var(--surface)' : 'var(--surface-3)',
                    border: '1px solid var(--line-strong)', color: 'var(--ink)',
                    fontSize: 12, fontWeight: 500, fontFamily: 'inherit',
                    // A native select auto-sizes to its WIDEST option, so with a
                    // few hundred boards it ballooned to the 200px cap and left
                    // a dead gap after the short "All audiences (n)" label. Pin
                    // it to one deliberate pill width instead and let the
                    // ellipsis handle the long board names.
                    width: 190, flexShrink: 0,
                    textOverflow: 'ellipsis', whiteSpace: 'nowrap', overflow: 'hidden',
                    boxShadow: '0 1px 2px rgba(11,18,32,.05)',
                  }}
                >
                  <option value={ALL_AUDIENCES_ID}>{t('campaignsRedesign.allAudiences', 'All audiences ({{n}})', { n: counts.all })}</option>
                  {audienceOptions.map((o) => (
                    <option key={o.id} value={o.id}>{o.name} ({o.count})</option>
                  ))}
                </select>
            )}

            {/* Date order. A two-state toggle rather than a dropdown: there are
                exactly two orderings, and one click to flip beats two to pick.
                The leading "Sort" label (FE#339) makes it read as a sort
                control, not a status chip. */}
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
              <span style={{ fontSize: 11.5, color: 'var(--muted)', fontWeight: 500 }}>{t('campaignsRedesign.sortLabel', 'Sort')}</span>
              <button
                type="button"
                onClick={() => setSort((s) => (s === 'date-desc' ? 'date-asc' : 'date-desc'))}
                aria-label={t('campaignsRedesign.sortAria', 'Sort by date, currently {{order}}. Activate to reverse.', { order: campaignSortLabel(t, sort) })}
                style={{
                  appearance: 'none', cursor: 'pointer', padding: '5px 11px', borderRadius: 999,
                  background: 'var(--surface)', border: '1px solid var(--line-strong)', color: 'var(--ink)',
                  fontSize: 12, fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: 6,
                  whiteSpace: 'nowrap', flexShrink: 0, boxShadow: '0 1px 2px rgba(11,18,32,.05)',
                }}
              >
                {sort === 'date-desc' ? <ArrowDown size={13} /> : <ArrowUp size={13} />}
                {campaignSortLabel(t, sort)}
              </button>
            </label>

            {/* Density (#162) — cards vs compact list. Hidden on mobile: the
                card layout is forced there, so the toggle is inert and only
                eats width. Labelled "View" for the same reason Sort is: an
                unlabelled icon pair reads as a status chip. */}
            {!isMobile && (
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                <span style={{ fontSize: 11.5, color: 'var(--muted)', fontWeight: 500 }}>{t('campaignsRedesign.viewLabel', 'View')}</span>
                <div style={{ display: 'inline-flex', borderRadius: 999, border: '1px solid var(--line-strong)', background: 'var(--surface)', padding: 2 }}>
                  {(['cards', 'rows'] as const).map((m) => (
                    <button
                      key={m}
                      type="button"
                      onClick={() => setView(m)}
                      aria-label={t('campaignsRedesign.viewAria', '{{mode}} view', { mode: campaignViewLabel(t, m) })}
                      aria-pressed={view === m}
                      style={{
                        appearance: 'none', cursor: 'pointer', padding: '4px 9px', borderRadius: 999, border: 0,
                        background: view === m ? 'var(--surface-3)' : 'transparent',
                        color: view === m ? 'var(--ink)' : 'var(--muted)',
                        display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11.5, fontWeight: 500,
                      }}
                    >
                      {m === 'cards' ? <LayoutGrid size={13} /> : <List size={13} />}
                      {campaignViewLabel(t, m)}
                    </button>
                  ))}
                </div>
              </label>
            )}
          </div>
        </div>
      </div>
      )}

      {/* #590: bulk-action toolbar — select-all + Pause/Stop selected, with a
          partial-failure report line. Shown only when there is at least one
          selectable (drip) campaign in the current view. */}
      {!isFirstRun && selectableIds.length > 0 && (
        <div style={{ padding: '10px 24px 0', width: '100%', maxWidth: CARD_COLUMN_MAX, margin: '0 auto', boxSizing: 'border-box' }}>
          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 12, padding: '8px 12px', border: '1px solid var(--line)', borderRadius: 10, background: 'var(--surface-2)' }}>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 12.5, fontWeight: 500, color: 'var(--ink-2)' }}>
              <input type="checkbox" checked={allSelected} onChange={toggleSelectAll} style={{ cursor: 'pointer' }} />
              {selectedCount > 0
                ? t('campaignsRedesign.bulk.selectedCount', '{{count}} selected', { count: selectedCount })
                : t('campaignsRedesign.bulk.selectAll', 'Select all')}
            </label>
            {selectedCount > 0 && (
              <>
                <button
                  type="button"
                  className="btn"
                  disabled={bulkBusy}
                  onClick={() => setBulkConfirm('pause')}
                >
                  {t('campaignsRedesign.bulk.pauseSelected', 'Pause selected')}
                </button>
                <button
                  type="button"
                  className="btn danger"
                  disabled={bulkBusy}
                  onClick={() => setBulkConfirm('stop')}
                >
                  {t('campaignsRedesign.bulk.stopSelected', 'Stop selected')}
                </button>
                <button type="button" className="btn ghost" disabled={bulkBusy} onClick={clearSelection}>
                  {t('campaignsRedesign.bulk.clear', 'Clear')}
                </button>
              </>
            )}
            {bulkNotice && <span style={{ fontSize: 12, color: 'var(--muted)' }}>{bulkNotice}</span>}
          </div>
          {bulkConfirm && (
            <MessageConfirmationDialog
              title={bulkConfirm === 'stop'
                ? t('campaignsRedesign.bulk.confirmStop', 'Stop {{count}} campaigns?', { count: selectedCount })
                : t('campaignsRedesign.bulk.confirmPause', 'Pause {{count}} campaigns?', { count: selectedCount })}
              description={bulkConfirm === 'stop'
                ? t('campaignsRedesign.bulk.confirmStopBody', 'Sending stops for anyone who has not received these yet. Messages already sent cannot be recalled.')
                : t('campaignsRedesign.bulk.confirmPauseBody', 'These campaigns stop sending until you resume them.')}
              confirmLabel={bulkConfirm === 'stop'
                ? t('campaignsRedesign.bulk.stopSelected', 'Stop selected')
                : t('campaignsRedesign.bulk.pauseSelected', 'Pause selected')}
              destructive={bulkConfirm === 'stop'}
              onClose={(value?: string) => { if (value !== undefined) void runBulk(bulkConfirm); else setBulkConfirm(null); }}
            />
          )}
        </div>
      )}

      {/* grid (or empty state) — VIRTUALIZED.
          The merged drips + standalone-campaigns set is a derived, client-sorted
          list (so it can't drive `LazyLoadingVirtuoso`, which owns its own
          single-collection selector). We therefore render through the SAME
          react-virtuoso primitive that `LazyLoadingVirtuoso` wraps, feeding it
          the already-merged `shown` array. Every LOADED card renders (the filter
          pills narrow the loaded set; nothing is truncated below what's in the
          store), only on-screen rows mount, and `endReached` pages in the next
          batch of campaigns on scroll.

          Layout: a single-column list of full-width `CampaignCard`s (the v1
          choice — a true masonry/multi-column grid inside virtuoso is not
          worth the complexity, and the cards already read well full-width). The
          14px inter-card gap is reproduced as a per-item bottom margin. */}
      {cards.length === 0 && !listLoaded ? (
        <div className="scroll" style={{ flex: 1, padding: '16px 24px 24px' }}>
          <div style={{ width: '100%', maxWidth: CARD_COLUMN_MAX, margin: '0 auto', boxSizing: 'border-box' }}>
            <ListSkeleton />
          </div>
        </div>
      ) : cards.length === 0 ? (
        <div className="scroll" style={{ flex: 1, padding: '0 24px 24px' }}>
          <FirstRunEmptyState
            icon={Megaphone}
            headline={t('campaignsRedesign.firstRun.headline', { defaultValue: 'Launch your first campaign' })}
            body={t('campaignsRedesign.firstRun.body', { defaultValue: 'Send one bulk message to a whole audience, or build a multi-step follow-up sequence on WhatsApp.' })}
            ctaLabel={t('campaignsRedesign.newCampaign', { defaultValue: 'New campaign' })}
            onCta={onNew}
            secondary={{ label: t('blueticksPanel.dripTemplates.fromTemplate', 'Start from a template'), icon: Bookmark, onSelect: onFromTemplate }}
            video={<GuideVideoButton lesson="first-campaign" variant="link" surface="campaigns_empty" />}
          />
        </div>
      ) : shown.length === 0 ? (
        <div className="scroll" style={{ flex: 1, padding: '16px 24px 24px' }}>
          <div style={{ maxWidth: 440, margin: '56px auto', textAlign: 'center', color: 'var(--muted)', fontSize: 13 }}>
            {t('campaignsRedesign.noMatches', { defaultValue: 'No campaigns match your filters' })}
          </div>
        </div>
      ) : (
        <Virtuoso
          style={{ flex: 1 }}
          data={shown}
          endReached={loadMoreCampaigns}
          increaseViewportBy={400}
          computeItemKey={(_index, c) => `${c.kind}-${c.id}`}
          // Column headers belong to the list, not to each row — Virtuoso's own
          // header slot keeps them out of the recycled item content.
          // MUST be an object, never `undefined`: Virtuoso dereferences
          // `components.EmptyPlaceholder` unconditionally, so an explicit
          // `undefined` throws and blanks the whole list — which is the DEFAULT
          // path, since `DEFAULT_CAMPAIGN_VIEW` is 'cards'. Both branches are
          // module-level constants so the reference is stable across renders
          // (a fresh literal would remount the list on every render).
          components={effectiveView === 'rows' ? ROWS_COMPONENTS : NO_COMPONENTS}
          itemContent={(index, c) =>
            effectiveView === 'rows' ? (
              // MERGE NOTE (#162 x #103): the compact row carries no overflow
              // menu by #162's design — it is a click-through to the detail
              // page. So it takes no `onEditContent`, and #103's "Edit message"
              // stays reachable in this density via the campaign detail header,
              // which is where the step-level editors live anyway.
              <div style={{ padding: '0 24px', width: '100%', maxWidth: CARD_COLUMN_MAX, margin: '0 auto', boxSizing: 'border-box' }}>
                <CampaignRow c={c} onOpen={onOpen} onEditContent={setEditingCampaignId} onEditSchedule={openCampaignPage} selectable={c.kind === 'drip'} selected={selectedIds.has(c.id)} onToggleSelect={toggleSelect} />
              </div>
            ) : (
              <div style={{ padding: `${index === 0 ? 16 : 0}px 24px 14px`, width: '100%', maxWidth: CARD_COLUMN_MAX, margin: '0 auto', boxSizing: 'border-box' }}>
                <CampaignCard c={c} onOpen={onOpen} onEditContent={setEditingCampaignId} onEditSchedule={openCampaignPage} selectable={c.kind === 'drip'} selected={selectedIds.has(c.id)} onToggleSelect={toggleSelect} />
              </div>
            )
          }
        />
      )}
        </>
      )}

      {/* #103. Two things here are load-bearing, not style:
          - `key={editingCampaignId}` — the modal latches its `begin` call in a
            ref that is NOT keyed on the campaign, so swapping the prop on a
            mounted instance would skip `begin` for the new campaign and leave
            the previous one's session state on screen. The key forces a fresh
            instance per campaign.
          - the modal is CONDITIONALLY RENDERED, never hidden behind an `open`
            flag. Its unmount cleanup is what sends the abort that resumes the
            campaign; keeping it mounted would strand the pause. (It refuses its
            own close while a call is in flight, so this can't unmount mid-save.)
          Rendered outside the tab switch so a stray tab change can't unmount it
          mid-edit. */}
      {editingCampaignId && (
        <CampaignContentEditModal
          key={editingCampaignId}
          campaignId={editingCampaignId}
          onClose={() => setEditingCampaignId(null)}
        />
      )}
    </div>
  );
};

export { CampaignsList };
export default CampaignsList;
